'use client'

import clsx from 'clsx'
import { CSSProperties, useEffect, useRef, useState } from 'react'

import S from './ExpandableText.module.scss'

interface ExpandableTextProps {
  text: string
  moreLabel: string
  lessLabel: string
  lines?: number
  className?: string
}

const ExpandableText = ({
  text,
  moreLabel,
  lessLabel,
  lines = 3,
  className
}: ExpandableTextProps) => {
  const ref = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let cancelled = false
    const measure = () => {
      const node = ref.current
      // Overflow is only meaningful while clamped; once expanded we keep the
      // toggle visible (see `showToggle`) instead of re-measuring the full text.
      if (cancelled || !node || expanded) return
      setIsOverflowing(node.scrollHeight > node.clientHeight + 1)
    }

    measure()

    let observer: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => measure())
      observer.observe(el)
    }
    // Re-measure once webfonts settle — FOUT/FOIT changes the wrapped line count.
    document.fonts?.ready?.then(() => measure())

    return () => {
      cancelled = true
      observer?.disconnect()
    }
  }, [text, lines, expanded])

  const showToggle = isOverflowing || expanded

  return (
    <div className={S.wrapper}>
      <p
        ref={ref}
        className={clsx(className, !expanded && S.clamped)}
        style={{ '--expandable-lines': lines } as CSSProperties}
      >
        {text}
      </p>

      {showToggle ? (
        <button
          type="button"
          className={S.toggle}
          aria-expanded={expanded}
          onClick={() => setExpanded(prev => !prev)}
        >
          <span>{expanded ? lessLabel : moreLabel}</span>
          <svg
            className={clsx(S.chevron, expanded && S.chevronUp)}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <path
              d="M16 10L12 14L8 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      ) : null}
    </div>
  )
}

export default ExpandableText
